import { Button, Grid, Paper } from '@material-ui/core'
import { Visibility, VisibilityOff } from '@material-ui/icons'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  completeAdminUserSignUp,
  getInviteUserDetails,
} from '../../../actions/userActions'
import locationsList from '../../../data/locations'
import { useInput } from '../../../hooks/useInput'
import { usePasswordValidator } from '../../../hooks/usePasswordValidator'
import AutoCompleteSelect from '../../Form/AutoCompleteSelect'
import FormContainer from '../../Form/FormContainer'
import TextInput from '../../Form/TextInput'
import Loader from '../../Loader/Loader'
import Message from '../../Message/Message'
import HelpTooltip from '../../Tooltip/HelpTooltip'
import SignIn from '../SignIn/SignIn'

const AcceptInvite = ({ match }) => {
  const dispatch = useDispatch()

  const id = match.params.id

  const { loading, error, user } = useSelector((state) => state.userDetails)
  const {
    loading: signUpLoading,
    error: signUpError,
    success: signUpSuccess,
  } = useSelector((state) => state.userAdminCompleteSignUp)

  const {
    value: firstName,
    bind: bindFirstName,
    error: firstNameError,
    setValue: setFirstName,
    setError: setFirstNameError,
  } = useInput()
  const {
    value: lastName,
    bind: bindLastName,
    error: lastNameError,
    setValue: setLastName,
    setError: setLastNameError,
  } = useInput('')
  const { bind: bindEmail, setValue: setEmail } = useInput('')
  const { value: phone, bind: bindPhone } = useInput('')
  const {
    value: password,
    bind: bindPassword,
    error: passwordError,
    setError: setPasswordError,
  } = useInput('')
  const {
    setValue: setLocation,
    value: location,
    setError: setLocationError,
    error: locationError,
  } = useInput('')
  const { value: confirmPassword, bind: bindConfirmPassword } = useInput('')
  const [showLoginLink, setShowLoginLink] = useState(false)
  const passwordIsValid = usePasswordValidator(password)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  useEffect(() => {
    if (id) {
      dispatch(getInviteUserDetails(id))
    }

    if (signUpSuccess) {
      setShowLoginLink(true)
    }
  }, [dispatch, id, signUpSuccess])

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName)
      setLastName(user.lastName)
      setEmail(user.email)
    }
  }, [dispatch, user, setFirstName, setLastName, setEmail])

  const formIsValid = () => {
    let ready = true

    if (firstName === '') {
      setFirstNameError('Please enter your first name.')
      ready = false
    } else {
      setFirstNameError('')
    }

    if (lastName === '') {
      setLastNameError('Please enter your last name.')
      ready = false
    } else {
      setLastNameError('')
    }

    // Only require location if admin is signing up
    if (user.isAdmin && location === '') {
      setLocationError('Please select a location')
      ready = false
    } else {
      setLocationError('')
    }

    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match')
      ready = false
    } else {
      setPasswordError('')
    }

    if (passwordIsValid !== '') {
      ready = false
      setPasswordError(passwordIsValid)
    }

    return ready
  }

  const submitHandler = (e) => {
    e.preventDefault()

    if (formIsValid()) {
      let adminUser = user

      adminUser.firstName = firstName
      adminUser.lastName = lastName
      adminUser.phone = phone
      adminUser.location = location
      adminUser.password = password

      dispatch(completeAdminUserSignUp(adminUser))
    }
  }

  return (
    <div
      style={{ background: 'white', minHeight: '100vh' }}
      className="sign-up-complete"
    >
      <FormContainer>
        <Paper
          elevation={3}
          className=" p-r-32 p-l-32 p-b-32 p-t-32"
          style={{ minHeight: 666 }}
        >
          {signUpError && <Message variant="error">{signUpError}</Message>}

          {error ? (
            <Message variant="error">{error}</Message>
          ) : loading || signUpLoading ? (
            <Loader />
          ) : !showLoginLink ? (
            <>
              <h2 className="m-b-30">Complete sign up</h2>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextInput
                    name="firstName"
                    label="First name"
                    bindValue={bindFirstName}
                    error={firstNameError}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextInput
                    name="lastName"
                    label="Last name"
                    bindValue={bindLastName}
                    error={lastNameError}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextInput
                    name="email"
                    type="email"
                    label="Email"
                    bindValue={bindEmail}
                    disabled={true}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextInput
                    type="tel"
                    name="phone"
                    label="Phone number"
                    bindValue={bindPhone}
                  />
                </Grid>

                {user.isAdmin && (
                  <Grid item xs={12}>
                    <AutoCompleteSelect
                      name="location"
                      label="Location"
                      options={locationsList}
                      value={location}
                      setValue={setLocation}
                      error={locationError}
                      minWidth={316}
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextInput
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    label={
                      <>
                        Password
                        <HelpTooltip
                          title={
                            <div>
                              <p className="m-b-0">Password requirements: </p>
                              <ul className="p-l-30">
                                <li>At least 8 characters</li>
                                <li>Upper and lowercase letters</li>
                                <li>
                                  Inclusion of at least one special character
                                </li>
                              </ul>
                            </div>
                          }
                        />
                      </>
                    }
                    bindValue={bindPassword}
                    error={passwordError}
                    endAdornment={true}
                    handleClick={() => setShowPassword(!showPassword)}
                    icon={showPassword ? <VisibilityOff /> : <Visibility />}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextInput
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    label="Confirm password"
                    bindValue={bindConfirmPassword}
                    error={passwordError}
                    endAdornment={true}
                    handleClick={() =>
                      setShowConfirmPassword(!showConfirmPassword)
                    }
                    icon={
                      showConfirmPassword ? <VisibilityOff /> : <Visibility />
                    }
                  />
                </Grid>
              </Grid>

              <div className="flex flex-end m-t-40">
                <Button
                  color="primary"
                  variant="contained"
                  onClick={submitHandler}
                >
                  Next
                </Button>
              </div>
            </>
          ) : (
            <>
              <h4 className="m-b-16">Account successfully created</h4>
              <h5 className="m-b-16">Please sign in</h5>
              <SignIn firstLogin={false} />
            </>
          )}
        </Paper>
      </FormContainer>
    </div>
  )
}

export default AcceptInvite
